import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { i18nLanguages, supportedLanguages } from '../locales';
import { stripLanguagePrefix } from '@/lib/languagePath';
import { languageTag, pageLanguagesForPath } from '@shared/pageLanguages';
import { consolidateCanonicalPath } from '@shared/canonicalConsolidation';
import {
  getSharedComponentRouteSeo,
  type SharedComponentRouteSeo,
} from '@shared/sharedComponentRouteSeo';

const BASE_URL = 'https://solmi.ai';

interface SEOConfig {
  title: string;
  description: string;
  keywords?: string | string[];
  canonical?: string;
  /**
   * Languages whose URL for this page is a page of its own; hreflang lists
   * only these (none when fewer than two). Defaults to every site language
   * plus the page languages that have this page (shared/pageLanguages.ts).
   * Mirrors ownPageLanguages in server/_core/seo.ts.
   */
  hreflangLanguages?: readonly string[];
  noIndex?: boolean;
  structuredData?: object | object[];
}

/**
 * SEO hook that handles:
 * - Document title
 * - Meta description
 * - Open Graph tags
 * - Twitter Card tags
 * - Canonical URL
 * - Hreflang tags for all supported languages
 * - Structured data (JSON-LD)
 */
/**
 * Per-route title/description for pages whose component serves several SEO
 * routes (DirectorHome, DownloadApp). Without this the component's single
 * hardcoded useSEO title overwrites the server-rendered one on hydration, and
 * Google — which renders JS — indexes every route in the cluster under the
 * same title. Returns null for routes that aren't in the shared map, so
 * callers fall back to their own default.
 */
export function useSharedComponentRouteSeo(): SharedComponentRouteSeo | null {
  const [location] = useLocation();
  // The page languages too: /th/ai-music-video-generator has its own head.
  return getSharedComponentRouteSeo(location, i18nLanguages);
}

/**
 * The robots tag the server sent with this document, when it says noindex,
 * and the path it was sent for.
 *
 * The server marks a URL it does not serve as a page noindex: a made-up
 * language prefix (/xx/ai-music-video-generator), a private page (/about,
 * which gets a stricter tag). The app's routes match more than the server's
 * (/:lang/... takes any first segment), so the page that renders there
 * called useSEO without noIndex and the tag was removed on hydration: a
 * crawler that runs JavaScript then saw an indexable copy of the page under
 * any prefix, self-canonical and with a full hreflang set (found Oct 14 2026
 * on /xx/ai-music-video-generator). A page that passed noIndex itself
 * replaced the server's stricter tag with the plain one.
 *
 * Read once, when this module loads, before anything here has touched the
 * tag. The path is the document's own (the navigation entry), not the
 * current location: this module arrives with the first page chunk that uses
 * it, which can be after a client-side navigation.
 */
export type ServerNoIndex = { pathname: string; content: string };

function readServerNoIndex(): ServerNoIndex | null {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return null;
  }
  const content =
    document.querySelector('meta[name="robots"]')?.getAttribute('content') ?? '';
  if (!/\bnoindex\b/i.test(content)) return null;
  let pathname = window.location.pathname;
  try {
    const entry = performance.getEntriesByType('navigation')[0];
    if (entry?.name) pathname = new URL(entry.name).pathname;
  } catch {
    // No Navigation Timing (old browser): the current path is the best guess.
  }
  return { pathname, content };
}

const SERVER_NO_INDEX = readServerNoIndex();

const withoutTrailingSlash = (pathname: string) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

/**
 * The robots tag a page should carry, or null for none (indexable).
 *  - On the URL the server sent as noindex: the server's own tag, whatever
 *    the page says. The app never makes such a URL indexable, and never
 *    weakens a stricter tag.
 *  - Elsewhere (reached by a client-side navigation, or a URL the server
 *    serves as indexable): the page's own choice, as before.
 */
export function robotsContentFor({
  noIndex,
  pathname,
  server,
}: {
  noIndex?: boolean;
  pathname: string;
  server: ServerNoIndex | null;
}): string | null {
  if (
    server &&
    withoutTrailingSlash(server.pathname) === withoutTrailingSlash(pathname)
  ) {
    return server.content;
  }
  return noIndex ? 'noindex, nofollow' : null;
}

export function useSEO(config: SEOConfig) {
  const [location] = useLocation();
  const { title, description, keywords, canonical, hreflangLanguages, noIndex, structuredData } =
    config;
  // Joined so a fresh array with the same languages doesn't re-run the effect.
  const hreflangKey = hreflangLanguages?.join(",");

  useEffect(() => {
    // Skip SEO updates if title is empty (parent component handles SEO)
    if (!title) return;

    // Update document title
    document.title = title;

    // Update meta description
    updateMetaTag('description', description);

    // Update meta keywords
    const keywordsContent = Array.isArray(keywords)
      ? keywords.join(', ')
      : keywords?.trim();
    if (keywordsContent) {
      updateMetaTag('keywords', keywordsContent);
    } else {
      removeMetaTag('keywords');
    }

    // Update Open Graph tags
    updateMetaProperty('og:title', title);
    updateMetaProperty('og:description', description);
    updateMetaProperty('og:url', canonical || getCanonicalUrl(location));

    // Update Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Update robots meta: noindex when the page asks for it, and always on
    // the URL the server itself sent as noindex (see robotsContentFor).
    const robots = robotsContentFor({
      noIndex,
      pathname: window.location.pathname,
      server: SERVER_NO_INDEX,
    });
    if (robots) {
      updateMetaTag('robots', robots);
    } else {
      removeMetaTag('robots');
    }

    // Update canonical link
    updateCanonicalLink(canonical || getCanonicalUrl(location));

    // Update hreflang links
    updateHreflangLinks(
      location,
      hreflangKey === undefined ? undefined : hreflangKey.split(","),
    );

    // Update structured data
    if (structuredData) {
      updateStructuredData(structuredData);
    }

    // Cleanup on unmount
    return () => {
      removeHreflangLinks();
      removeStructuredData('seo-structured-data');
    };
  }, [title, description, keywords, canonical, hreflangKey, noIndex, location, structuredData]);
}

/**
 * Get the canonical URL for the current path.
 *
 * Self-referencing per language, mirroring the server's `withLanguagePrefix`
 * (server/_core/seo.ts). It used to strip the language prefix, so once a
 * localized page hydrated, /es/bpm-finder declared https://solmi.ai/bpm-finder
 * as its canonical. Google renders JS, so every localized URL told it "I am a
 * duplicate of the English page", which voids hreflang (hreflang requires a
 * self canonical) and works against indexing the locale pages at all.
 */
export function getCanonicalUrl(path: string): string {
  const pathParts = path.split('/').filter(Boolean);
  const potentialLang = pathParts[0];

  // A page language on one of its own pages (/th/ai-music-video-generator)
  // is self-canonical like any other language version.
  const page = stripLanguagePrefix(path.split(/[?#]/)[0] || '/');
  if (page.hasLang && !supportedLanguages.includes(page.lang as any)) {
    return `${BASE_URL}/${page.lang}${normalizeSeoPath(page.pathname)}`;
  }

  if (supportedLanguages.includes(potentialLang as any) && potentialLang !== 'en') {
    const canonicalPath = normalizeSeoPath('/' + pathParts.slice(1).join('/'));
    if (canonicalPath === '/') return `${BASE_URL}/${potentialLang}/`;
    return `${BASE_URL}/${potentialLang}${canonicalPath}`;
  }
  if (potentialLang === 'en') {
    return `${BASE_URL}${normalizeSeoPath('/' + pathParts.slice(1).join('/'))}`;
  }

  return `${BASE_URL}${normalizeSeoPath(path)}`;
}

/**
 * Get the path without language prefix
 */
function getPathWithoutLang(path: string): string {
  const pathParts = path.split('/').filter(Boolean);
  const potentialLang = pathParts[0];

  if (supportedLanguages.includes(potentialLang as any)) {
    return normalizeSeoPath('/' + pathParts.slice(1).join('/'));
  }

  // A page language on one of its own pages.
  const page = stripLanguagePrefix(path.split(/[?#]/)[0] || '/');
  if (page.hasLang) return normalizeSeoPath(page.pathname);

  return normalizeSeoPath(path);
}

function normalizeSeoPath(path: string): string {
  const withoutQuery = path.split("?")[0]?.split("#")[0] || "/";
  if (!withoutQuery || withoutQuery === "/") return "/";

  const normalized = withoutQuery.endsWith("/") ? withoutQuery.slice(0, -1) : withoutQuery;

  // Keep blog listing URLs on the trailing-slash canonical to avoid /blog -> /blog/ redirect leakage.
  if (normalized === "/blog") return "/blog/";

  // Collapse thin /tools/* duplicates onto their rich top-level twin so the
  // JS-rendered canonical matches the server-rendered one (see
  // shared/canonicalConsolidation.ts).
  return consolidateCanonicalPath(normalized);
}

function updateMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeMetaTag(name: string) {
  const meta = document.querySelector(`meta[name="${name}"]`);
  if (meta) {
    meta.remove();
  }
}

function updateMetaProperty(property: string, content: string) {
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function updateCanonicalLink(url: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

/**
 * The hreflang alternates of a page, in the order they are written: one per
 * language, then x-default (the English version). None when the page has
 * fewer than two languages.
 *
 * `languages` defaults to the 14 site languages plus the page languages that
 * have this page (shared/pageLanguages.ts: Thai and Traditional Chinese on
 * the music video page), which is the cluster the server prints
 * (ownPageLanguages in server/_core/seo.ts). Without the page languages
 * here, hydration would drop the two from the cluster the server sent, on
 * all 16 versions of that page. The attribute is the language's tag: its
 * code, except zh-Hant for Traditional Chinese (/tw/). Pure, so
 * server/_core/seoPageLanguages.test.ts compares it with the server's head.
 */
export function hreflangAlternates(
  currentPath: string,
  languages?: readonly string[],
): Array<{ hreflang: string; href: string }> {
  const basePath = getPathWithoutLang(currentPath);
  const list = languages ?? [
    ...supportedLanguages,
    ...pageLanguagesForPath(basePath),
  ];
  if (list.length < 2) return [];
  return [
    // English uses base path, other languages use /{lang}/path
    ...list.map((lang) => ({
      hreflang: languageTag(lang),
      href:
        lang === 'en'
          ? `${BASE_URL}${basePath || '/'}`
          : `${BASE_URL}/${lang}${basePath}`,
    })),
    // x-default points to the English version
    { hreflang: 'x-default', href: `${BASE_URL}${basePath || '/'}` },
  ];
}

function updateHreflangLinks(currentPath: string, languages?: readonly string[]) {
  // Remove existing hreflang links
  removeHreflangLinks();

  for (const { hreflang, href } of hreflangAlternates(currentPath, languages)) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('data-hreflang', 'true'); // Mark for easy removal
    link.setAttribute('href', href);
    document.head.appendChild(link);
  }
}

function removeHreflangLinks() {
  const existingLinks = document.querySelectorAll(
    'link[data-hreflang="true"], link[rel="alternate"][hreflang]'
  );
  existingLinks.forEach((link) => link.remove());
}

function updateStructuredData(data: object | object[]) {
  removeStructuredData('seo-structured-data');

  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('id', 'seo-structured-data');
  script.textContent = JSON.stringify(Array.isArray(data) ? data : [data]);
  document.head.appendChild(script);
}

function removeStructuredData(id: string) {
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }
}

export default useSEO;
